import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "./Navbar/Navbar";
import { motion, AnimatePresence } from "framer-motion";
import { questionsBank } from "./QuizData2";
import { BsCheckCircleFill } from "react-icons/bs";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../hooks/useAuth";
import { useSubmitQuizMutation } from "../Redux/app/appApis";

const STEPS = [
  "Choose Module",
  "Select Certification",
  "Attend Quiz",
  "Google Review",
  "Download Certificate",
];

const MAX_ATTEMPTS = 3;

const Module2Quiz = () => {
  const { emailAddress } = useAuth();
  const { certification } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [attemptsMade, setAttemptsMade] = useState(0);
  const [steps, setSteps] = useState(
    location.state?.steps || [false, false, false, false, false],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [currentQuestion, setCurrentQuestion] = useState(0);

  const [submitQuiz] = useSubmitQuizMutation();

  useEffect(() => {
    const selectedQuestions = questionsBank[certification] || [];
    if (selectedQuestions.length === 0) {
      toast.error("No questions found for this certification.");
      navigate("/home");
      return;
    }
    setQuestions(selectedQuestions);
  }, [certification, navigate]);
  useEffect(() => {
    const sessionKey = `quiz_attempts_${certification}`;
    const savedAttempts = sessionStorage.getItem(sessionKey);
    if (savedAttempts) {
      setAttemptsMade(parseInt(savedAttempts, 10));
    }
  }, [certification]);

  const handleAnswerChange = (questionIndex, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (attemptsMade >= MAX_ATTEMPTS) {
      toast.error(
        "You have reached the maximum number of attempts (3). Please logout and login again to retry.",
        {
          position: "top-center",
          autoClose: 5000,
        },
      );
      setTimeout(() => {
        navigate("/home");
      }, 4000);
      return;
    }

    const allAnswered = questions.every((_, index) => answers[index]);
    if (!allAnswered) {
      toast.warning("Please answer all questions before submitting.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const answerArray = questions.map((_, index) => answers[index] || null);
      const quizType = certification;
      const response = await submitQuiz({
        emailAddress,
        quizType,
        answers: answerArray,
      }).unwrap();

      const newAttemptCount = attemptsMade + 1;
      setAttemptsMade(newAttemptCount);
      sessionStorage.setItem(
        `quiz_attempts_${certification}`,
        newAttemptCount.toString(),
      );

      setQuizResult({
        score: response.score,
        totalQuestions: response.totalQuestions,
        result: response.result,
        message: response.message,
      });

      setSteps((prevSteps) => {
        const updatedSteps = [...prevSteps];
        updatedSteps[2] = true;
        if (response.result === "Passed") {
          updatedSteps[3] = true;
        }
        return updatedSteps;
      });

      if (response.result === "Passed") {
        toast.success(
          `Congratulations! You passed with ${response.score}/${response.totalQuestions}`,
          {
            position: "top-center",
            autoClose: 5000,
          },
        );
      } else {
        const attemptsLeft = MAX_ATTEMPTS - newAttemptCount;
        if (attemptsLeft > 0) {
          toast.error(
            `You scored ${response.score}/${response.totalQuestions}. You must retake the quiz. Attempts left: ${attemptsLeft}`,
            {
              position: "top-center",
              autoClose: 5000,
            },
          );
        } else {
          toast.error(
            `You scored ${response.score}/${response.totalQuestions}. Maximum attempts exceeded. Please logout and login again.`,
            {
              position: "top-center",
              autoClose: 5000,
            },
          );
        }
      }
    } catch (error) {
      console.error("Quiz submission error:", error);
      toast.error(
        error?.data?.message || "Failed to submit quiz. Please try again.",
        {
          position: "top-center",
          autoClose: 5000,
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleRetakeTest = () => {
    if (attemptsMade >= MAX_ATTEMPTS) {
      toast.error(
        "Maximum attempts exceeded. Please logout and login again to retry.",
        {
          position: "top-center",
          autoClose: 3000,
        },
      );
      setTimeout(() => {
        navigate("/home");
      }, 3000);
      return;
    }

    setQuizResult(null);
    setAnswers({});
  };

  const handleCloseResult = () => {
    setQuizResult(null);
    if (quizResult?.result === "Passed") {
      navigate("/googleReview", {
        state: {
          certification,
          score: quizResult.score,
          totalQuestions: quizResult.totalQuestions,
        },
      });
    }
  };

  const breadcrumbVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-100/90 via-white/70 to-red-100/90">
      <Navbar />

      <div className="flex flex-col items-center mt-6 mb-10">
        {/* STEPPER */}
        <div className="flex justify-center mt-2 mb-4 px-2">
          <div className="flex items-center bg-white/70 backdrop-blur-md shadow-xl rounded-full p-2 flex-wrap gap-2">
            {STEPS.map((stepLabel, index) => (
              <motion.div
                key={index}
                className="flex items-center"
                initial="initial"
                animate="animate"
                variants={breadcrumbVariants}
                transition={{ duration: 0.4, delay: index * 0.15 }}
              >
                {/* STEP PILL */}
                <div
                  className={`flex items-center px-4 py-2 rounded-full text-xs sm:text-sm md:text-base font-medium transition-all duration-300
              ${
                steps[index]
                  ? "bg-green-500 text-white"
                  : index === 0
                    ? "bg-gradient-to-r from-red-500 to-red-700 text-white shadow-md"
                    : "bg-gray-200 text-gray-600"
              }`}
                >
                  {/* STEP CIRCLE */}
                  <span
                    className={`mr-2 flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                ${
                  steps[index]
                    ? "bg-white text-green-500"
                    : index === 0
                      ? "bg-white text-red-600"
                      : "bg-gray-300 text-gray-700"
                }`}
                  >
                    {steps[index] ? <BsCheckCircleFill /> : index + 1}
                  </span>

                  {stepLabel}
                </div>

                {/* CONNECTOR LINE */}
                {index < STEPS.length - 1 && (
                  <div className="hidden md:block w-6 h-[2px] bg-gray-300 mx-1"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        <h1 className="text-center text-2xl md:text-3xl font-bold mt-6  text-gray-800">
          ISO Certification Quiz
        </h1>

        <div className="text-center text-gray-500 mb-6 ">
          <p className="text-sm">
            {certification.toUpperCase()} • Attempts: {attemptsMade} /{" "}
            {MAX_ATTEMPTS}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="min-w-[1000px] mx-auto">
          {questions.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 mb-6">
              <p className="text-lg font-semibold text-gray-800 mb-6">
                {currentQuestion + 1}. {questions[currentQuestion].question}
              </p>
              <div className="space-y-4">
                {questions[currentQuestion].options?.map((option, idx) => {
                  const optionValue = String.fromCharCode(65 + idx); // A, B, C, D
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition
                    ${
                      answers[currentQuestion] === optionValue
                        ? "bg-blue-50 border-blue-500 shadow-sm"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    }`}
                    >
                      <input
                        type="radio"
                        id={`q${currentQuestion}_option${idx}`}
                        name={`q${currentQuestion}`}
                        value={optionValue}
                        checked={answers[currentQuestion] === optionValue}
                        onChange={() =>
                          handleAnswerChange(currentQuestion, optionValue)
                        }
                        className="text-blue-600 w-4 h-auto "
                        disabled={isSubmitting}
                      />
                      <label
                        htmlFor={`q${currentQuestion}_option${idx}`}
                        className="text-gray-700 cursor-pointer flex-1"
                      >
                        <span className="text-gray-700 font-medium">
                          {optionValue}.
                        </span>{" "}
                        {option}
                      </label>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between mt-8 gap-4">
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className="flex-1  border border-gray-300 bg-gray-300 text-black font-medium py-3 rounded-lg disabled:opacity-50"
                >
                  ← Previous
                </button>

                {currentQuestion === questions.length - 1 ? (
                  <button
                    type="submit"
                    disabled={isSubmitting || attemptsMade >= MAX_ATTEMPTS}
                    className={`flex-1 py-3 rounded-lg font-medium transition duration-300 ${
                      isSubmitting || attemptsMade >= MAX_ATTEMPTS
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-800 to-purple-900 text-white"
                    }`}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Quiz"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (!answers[currentQuestion]) {
                        toast.warning(
                          "Please select an option before continuing",
                        );
                        return;
                      }
                      handleNext();
                    }}
                    className="flex-1 py-3 rounded-lg text-white bg-gradient-to-r from-blue-800 to-purple-900"
                  >
                    Next →
                  </button>
                )}
              </div>
            </div>
          )}
        </form>

        <AnimatePresence>
          {quizResult && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className={` bg-white p-8 text-center rounded-lg  w-full max-w-lg shadow-2xl ${
                  quizResult.result === "Passed"
                    ? "bg-green-50 border-2 border-green-500"
                    : "bg-red-50 border-2 border-red-500"
                }`}
              >
                <h2
                  className={`text-2xl font-bold mb-4 text-center ${
                    quizResult.result === "Passed"
                      ? "text-green-700"
                      : "text-red-700"
                  }`}
                >
                  {quizResult.result === "Passed"
                    ? "🎉 Congratulations!"
                    : "❌ Quiz Failed"}
                </h2>

                <div className="text-center mb-6">
                  <p className="text-lg sm:text-xl text-gray-700 font-semibold mb-2">
                    Your Score
                  </p>
                  <p
                    className={`text-4xl sm:text-5xl font-bold ${
                      quizResult.result === "Passed"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {quizResult.score} / {quizResult.totalQuestions}
                  </p>
                </div>

                <p className="text-center text-gray-600 mb-6">
                  {quizResult.result === "Passed"
                    ? "You have successfully passed the quiz!"
                    : `You need to score at least ${Math.ceil(quizResult.totalQuestions / 2)} to pass.`}
                </p>

                {quizResult.result === "Failed" && (
                  <p className="text-center text-sm text-gray-500 mb-6">
                    Attempts remaining: {MAX_ATTEMPTS - attemptsMade}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {quizResult.result === "Passed" ? (
                    <button
                      onClick={handleCloseResult}
                      className="bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition duration-300 font-medium"
                    >
                      Continue to Google Review
                    </button>
                  ) : (
                    <>
                      {attemptsMade < MAX_ATTEMPTS ? (
                        <button
                          onClick={handleRetakeTest}
                          className="bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 transition duration-300 font-medium"
                        >
                          Retake Quiz
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate("/home")}
                          className="bg-gray-600 text-white py-3 px-6 rounded-lg hover:bg-gray-700 transition duration-300 font-medium"
                        >
                          Back to Home
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => setQuizResult(null)}
                    className="bg-gray-200 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition duration-300 font-medium"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Module2Quiz;
